// src/app/modules/user/components/common/PatientDetailBadges.tsx

import React, {useEffect, useState, useCallback} from 'react'
import {Badge, Spinner} from 'react-bootstrap'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {formatBillingCycleDateToUser} from '../../helper/dateTimeHelpers'
import {BillingCycleModel} from '../../../../entities/entities'
import {useReduxAuth} from '../../../auth'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'

interface PatientDetailBadgesProps {
  patientUUID: string
}

const PatientDetailBadges: React.FC<PatientDetailBadgesProps> = ({patientUUID}) => {
  const {currentUser} = useReduxAuth()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  const [patientData, setPatientData] = useState<{
    billingCycle: any | null // TODO::Make this a BillingCycleModel
    careMinutes: number | null
    readings: number | null
    cycleStatus: string | null
  }>({
    billingCycle: null,
    careMinutes: null,
    readings: null,
    cycleStatus: null
  })
  const [loading, setLoading] = useState(false)

  // Fetch all patient data in one API call
  const fetchPatientData = useCallback(async () => {
    if (!patientUUID) return
    
    setLoading(true)
    
    try {
      // Build query params - include doctor_id for MA
      const doctorIdParam = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? `doctor_id=${maSelectedDoctorId}`
        : null
      // Use the new combined API endpoint
      const response = await axios.get<{
        status: string
        data: {
          latest_cycle: BillingCycleModel | null
        }
      }>(API_ENDPOINTS.GET_PATIENT_LATEST_BILLING_CYCLE(patientUUID, doctorIdParam), {
        validateStatus: (status) => status < 500,
      })

      if (response.status === 200 && response.data?.data?.latest_cycle) {
        const { latest_cycle } = response.data.data
        
        setPatientData({
          billingCycle: {
            start: latest_cycle.cycle_start,
            end: latest_cycle.cycle_end
          },
          careMinutes: parseFloat(latest_cycle.care_minutes),
          readings: latest_cycle.readings,
          cycleStatus: latest_cycle.status
        })
      }
    } catch (err: any) {
      // Silently handle auth errors
      console.log('Could not fetch patient summary data:', err.message)
    } finally {
      setLoading(false)
    }
  }, [patientUUID, roleId, maSelectedDoctorId])

  // Initial data fetch
  useEffect(() => {
    fetchPatientData()
  }, [fetchPatientData])

  // Only show dates if we have a billing cycle
  const startDate = patientData.billingCycle?.start ? formatBillingCycleDateToUser(patientData.billingCycle.start) : null
  const endDate = patientData.billingCycle?.end ? formatBillingCycleDateToUser(patientData.billingCycle.end) : null
  
  // Calculate background color once based on cycle status
  const badgeBgClass = patientData.cycleStatus === 'active' ? 'bg-primary' : 'bg-secondary'

  if (loading) {
    return (
      <div className='d-flex gap-2'>
        <Badge pill className='bg-info text-white d-flex align-items-center'>
          <Spinner animation='border' size='sm' className='me-2' />
          Loading...
        </Badge>
      </div>
    )
  }

  return (
    <div className='d-flex gap-2'>
      {patientData.billingCycle && startDate && endDate && (
        <span className={`badge badge-lg badge-pill ${badgeBgClass}`}>
          {startDate} – {endDate}
        </span>
      )}

      {/* Always show minutes */}
      <span className={`badge badge-lg badge-pill ${badgeBgClass}`}>{patientData.careMinutes ?? 0} min</span>

      {/* Only show readings if billing cycle exists */}
      {patientData.billingCycle && (
        <span className={`badge badge-lg badge-pill ${badgeBgClass}`}>{patientData.readings ?? 0} readings</span>
      )}
    </div>
  )
}

export default PatientDetailBadges
